import React from 'react'
import HighlightedText from '../../../Utils/HighlightedText'
import styles from './styles.module.css'
import Spacer from '../../../Spacer'
import CtaV2 from '../../../CtaV2'
import Img from '../../../Img' // task #67: canonical image component
import Youtube from '../../../Youtube'
import Text from '../../../Text'
import { $textPrimary } from '../../../../styles/colors/semantics'
import { sectionAccessibleName, ctaHiddenSuffix } from '../../../../utils/ctaAccessibleName'

const cx = (...c) => c.filter(Boolean).join(' ')

function FfbNextGen(props) {
  const { backgroundImage,
      compact,
      subtitleHl, titleHl, textHl, ctaV2,
      card1_title, card1_text,
      card2_title, card2_text, card2_icon,
      card3_title, card3_text, card3_icon,
      card4_title, card4_text,
      card5_title, card5_text, youtubeUrl,
      youtubeTitle, youtubeText,
      isPageHero = false
  } = props

  // task #154: name the generic CTA verb ("Learn more") by this section so its
  // accessible name is descriptive (Lighthouse link-text).
  const ctaSuffix = ctaHiddenSuffix(ctaV2?.title, sectionAccessibleName(titleHl))

  // Single-<h1> rule (Audit A #1): ffbNextGen is the hero on /nextgenffb but ALSO
  // a mid-page block on the home + wheel-base pages. Promote its title to the
  // document <h1> ONLY when it is the page hero (`isPageHero`, the first visible
  // section) so the non-hero occurrences add no stray <h1>. `.h1-contents` adds no
  // box, so the visible design is byte-identical either way.
  const titleHero = <HighlightedText {...titleHl} highlightExactMatch={true}/>
  const titleNode = isPageHero ? <h1 className="h1-contents">{titleHero}</h1> : titleHero

  if (compact) {
    return (
      <section className="rounded-[32px] mt-4 mx-4 px-[5%] py-12 md:py-16 lg:py-20 flex items-center justify-center relative bg-black overflow-hidden">
        <div className={styles.backgroundImageWrapper}>
          {/* task #67: descriptor via <Img> */}
          <Img
            image={backgroundImage.image}
            alt={backgroundImage.alt || ''}
            priority={true}
            objectFit="cover"
            reserveSpace={false}
          />
          <div className={styles.backgroundGradientOverlay} />
        </div>
        <div className="container z-1">
          <div className="mx-auto w-full max-w-xl text-center items-center justify-center flex flex-col gap-8">
            <div className="items-center justify-center flex flex-col gap-4">
              {titleNode}
            </div>
            <HighlightedText {...textHl} />
            <CtaV2 {...ctaV2} hiddenSuffix={ctaSuffix} />
          </div>
        </div>
      </section>
      )
  } else {
    return (
      <section className="px-[5%] py-16 md:py-24 lg:py-28 flex items-center justify-center relative bg-black overflow-hidden">
        <div className={cx(styles.backgroundImageWrapper, styles.backgroundImageWrapperFullHeight)}>
          {/* task #67: descriptor via <Img> */}
          <Img
            image={backgroundImage.image}
            alt={backgroundImage.alt || ''}
            priority={true}
            objectFit="cover"
            reserveSpace={false}
          />
          <div className={styles.backgroundGradientOverlay} />
        </div>
        <div className="container z-1">
          <div className="mx-auto mb-12 w-full max-w-3xl text-center items-center justify-center flex flex-col gap-8 md:mb-18 lg:mb-20">
            <div className="items-center justify-center flex flex-col gap-4">
              {titleNode}
              {/*<HighlightedText {...subtitleHl} highlightExactMatch={true}/>*/}
            </div>
            <HighlightedText {...textHl} />
            <CtaV2 {...ctaV2} hiddenSuffix={ctaSuffix} />
          </div>
          <div className={styles.columnsWrapper}>
            <div className={styles.leftColumn}>
              <div className={styles.bigCard}>
                <div className="p-6 md:p-12 flex flex-col gap-6 box-border w-full">
                  <HighlightedText {...card1_title} highlightExactMatch={true}/>
                  <HighlightedText {...card1_text} highlightExactMatch={true}/>
                </div>
                <div className="w-full">
                  { renderImage1() }
                </div>
              </div>
              <div className="flex gap-4 box-border w-full">
                <div className={styles.smallCard}>
                  <div className="h-12 w-12 relative">
                    {/* task #67: descriptor via <Img> */}
                    <Img
                      image={card2_icon.image}
                      alt={card2_icon.alt}
                      objectFit="contain"
                    />
                  </div>
                  <HighlightedText {...card2_title}/>
                  <HighlightedText {...card2_text}/>
                  <Spacer size={24}/>
                </div>
                <div className={styles.smallCard}>
                  <div className="h-12 w-12 relative">
                    {/* task #67: descriptor via <Img> */}
                    <Img
                      image={card3_icon.image}
                      alt={card3_icon.alt}
                      objectFit="contain"
                    />
                  </div>
                  <HighlightedText {...card3_title}/>
                  <HighlightedText {...card3_text}/>
                  <Spacer size={24}/>
                </div>
              </div>
            </div>
            <div className={styles.leftColumn}>
              <div className="flex gap-4 box-border w-full">
                <div className={styles.smallCard}>
                  <div className="grid grid-cols-2 place-items-center">
                    { renderImage3() }
                    <div className="p-6 flex flex-col gap-4">
                      <HighlightedText {...card4_title}/>
                      <HighlightedText {...card4_text}/>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.bigCard}>
                <div className="p-6 md:p-12 flex flex-col gap-6 box-border w-full">
                  <HighlightedText {...card5_title}/>
                  <HighlightedText {...card5_text}/>
                </div>
                <div className="w-full">
                  { renderImage2() }
                </div>
              </div>
            </div>
          </div>
          {
            youtubeUrl && <div className="h-fit w-full box-border mt-8 rounded-[32px] flex flex-col md:flex-row  border border-white/16 items-center">
              <div className="w-full md:w-4/9 overflow-hidden rounded-t-[32px] md:rounded-l-[32px] md:rounded-t-[0px]">
                <Youtube url={youtubeUrl} height={250}/>
              </div>
              <div className="w-full flex flex-col gap-2 p-8 box-border">
                  <HighlightedText {...youtubeTitle}/>
                  <HighlightedText {...youtubeText}/>
                  <a href={youtubeUrl} className="no-underline">
                    <Text type="regular" color={$textPrimary.dark}>Watch the video</Text>
                  </a>
              </div>
            </div>
          }
        </div>
      </section>
    )
  }
}

function renderImage1(){
  return (
      <svg viewBox="0 0 592 338" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M-0.5 169H554.5" stroke="url(#paint0_linear_8852_7178)" strokeWidth="16" strokeLinecap="round"/>
        <path opacity="0.8" d="M0 137H390" stroke="url(#paint1_linear_8852_7178)" strokeWidth="8" strokeLinecap="round"/>
        <path opacity="0.8" d="M0 201H463" stroke="url(#paint2_linear_8852_7178)" strokeWidth="8" strokeLinecap="round"/>
        <path opacity="0.6" d="M0 222H296" stroke="url(#paint3_linear_8852_7178)" strokeWidth="4" strokeLinecap="round"/>
        <path opacity="0.6" d="M0 116H352" stroke="url(#paint4_linear_8852_7178)" strokeWidth="4" strokeLinecap="round"/>
        <path opacity="0.3" d="M0 95H167" stroke="url(#paint5_linear_8852_7178)" strokeWidth="2" strokeLinecap="round"/>
        <path opacity="0.3" d="M0 243H241" stroke="url(#paint6_linear_8852_7178)" strokeWidth="2" strokeLinecap="round"/>
        <defs>
          <linearGradient id="paint0_linear_8852_7178" x1="-0.5" y1="169.5" x2="554.5" y2="169.5" gradientUnits="userSpaceOnUse">
            <stop/>
            <stop offset="0.8" stopColor="#F4F4F4"/>
            <stop offset="1" stopColor="#E11D48"/>
          </linearGradient>
          <linearGradient id="paint1_linear_8852_7178" x1="0" y1="137.5" x2="390" y2="137.5" gradientUnits="userSpaceOnUse">
            <stop/>
            <stop offset="1" stopColor="#F4F4F4"/>
          </linearGradient>
          <linearGradient id="paint2_linear_8852_7178" x1="0" y1="201.5" x2="463" y2="201.5" gradientUnits="userSpaceOnUse">
            <stop/>
            <stop offset="1" stopColor="#F4F4F4"/>
          </linearGradient>
          <linearGradient id="paint3_linear_8852_7178" x1="0" y1="222.5" x2="296" y2="222.5" gradientUnits="userSpaceOnUse">
            <stop/>
            <stop offset="1" stopColor="#F4F4F4"/>
          </linearGradient>
          <linearGradient id="paint4_linear_8852_7178" x1="0" y1="116.5" x2="352" y2="116.5" gradientUnits="userSpaceOnUse">
            <stop/>
            <stop offset="1" stopColor="#F4F4F4"/>
          </linearGradient>
          <linearGradient id="paint5_linear_8852_7178" x1="0" y1="95.5" x2="167" y2="95.5" gradientUnits="userSpaceOnUse">
            <stop/>
            <stop offset="1" stopColor="#F4F4F4"/>
          </linearGradient>
          <linearGradient id="paint6_linear_8852_7178" x1="0" y1="243.5" x2="241" y2="243.5" gradientUnits="userSpaceOnUse">
            <stop/>
            <stop offset="1" stopColor="#F4F4F4"/>
          </linearGradient>
        </defs>
      </svg>

    )
}

function renderImage2() {
  return (
      <svg viewBox="0 0 592 360" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.5 235H591.5" stroke="url(#paint0_linear_8852_7223)" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 8"/>
          <g opacity="0.25">
              <path d="M586 296.5H444L296 52.5L148 296.5H6" stroke="url(#paint1_linear_8852_7223)" strokeWidth="32" strokeLinecap="round"/>
              <path d="M586 296.5H444L296 52.5L148 296.5H6" stroke="black" strokeOpacity="0.2" strokeWidth="32" strokeLinecap="round"/>
          </g>
          <path d="M6 296.5H94.2196C129.499 296.5 164.206 287.576 195.11 270.558C257.923 235.968 334.077 235.968 396.89 270.558C427.794 287.576 462.501 296.5 497.78 296.5H586" stroke="url(#paint2_linear_8852_7223)" strokeWidth="8" strokeLinecap="round"/>
          <g filter="url(#filter0_f_8852_7223)">
              <ellipse cx="296" cy="235" rx="86" ry="10" fill="#E11D48"/>
          </g>
          <defs>
              <filter id="filter0_f_8852_7223" x="130" y="145" width="332" height="180" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feGaussianBlur stdDeviation="40" result="effect1_foregroundBlur_8852_7223"/>
              </filter>
              <linearGradient id="paint0_linear_8852_7223" x1="8.5" y1="235.5" x2="591.5" y2="235.5" gradientUnits="userSpaceOnUse">
                  <stop/>
                  <stop offset="0.4" stopColor="#F4F4F4"/>
                  <stop offset="0.5" stopColor="#E11D48"/>
                  <stop offset="0.6" stopColor="#F4F4F4"/>
                  <stop offset="1"/>
              </linearGradient>
              <linearGradient id="paint1_linear_8852_7223" x1="586" y1="195.75" x2="6" y2="195.75" gradientUnits="userSpaceOnUse">
                  <stop/>
                  <stop offset="0.5" stopColor="#F4F4F4"/>
                  <stop offset="1"/>
              </linearGradient>
              <linearGradient id="paint2_linear_8852_7223" x1="6" y1="195.75" x2="586" y2="195.75" gradientUnits="userSpaceOnUse">
                  <stop/>
                  <stop offset="0.5" stopColor="#F4F4F4"/>
                  <stop offset="1"/>
              </linearGradient>
          </defs>
      </svg>

  )
}

function renderImage3() {
  return (
      <svg viewBox="0 0 296 340" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle opacity="0.5" cx="148" cy="170" r="111" stroke="url(#paint0_linear_8852_7201)" strokeWidth="4" strokeLinecap="round"/>
          <path d="M139 170H74" stroke="#F4F4F4" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 8"/>
          <path d="M148.004 161.005L148.004 96.0049" stroke="#F4F4F4" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 8"/>
          <path d="M222 170H157" stroke="#F4F4F4" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 8"/>
          <path d="M148.004 244.005L148.004 179.005" stroke="#F4F4F4" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 8"/>
          <circle cx="148" cy="170" r="4" fill="#E11D48"/>
          <g filter="url(#filter0_f_8852_7201)">
              <circle cx="148" cy="169" r="16" fill="#E11D48"/>
          </g>
          <defs>
              <filter id="filter0_f_8852_7201" x="68" y="89" width="160" height="160" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feGaussianBlur stdDeviation="32" result="effect1_foregroundBlur_8852_7201"/>
              </filter>
              <linearGradient id="paint0_linear_8852_7201" x1="148" y1="281" x2="148" y2="59" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#F4F4F4"/>
                  <stop offset="0.4"/>
                  <stop offset="0.6"/>
                  <stop offset="1" stopColor="#F4F4F4"/>
              </linearGradient>
          </defs>
      </svg>

  )
}

export default FfbNextGen
